import { useEffect, useState, useCallback, useRef } from 'react';
import * as cornerstone from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { initializeCornerstone } from '@/services/cornerstoneInit';

const SHARED_ENGINE_ID = 'shared-viewer-engine';

interface UseSharedRenderingEngineReturn {
  renderingEngine: CornerstoneTypes.IRenderingEngine | null;
  renderingEngineId: string;
  isInitialized: boolean;
  error: Error | null;
  addViewport: (
    viewportId: string,
    element: HTMLDivElement,
    imageIds?: string[]
  ) => Promise<CornerstoneTypes.IStackViewport | null>;
  removeViewport: (viewportId: string) => void;
  getViewport: (viewportId: string) => CornerstoneTypes.IStackViewport | null;
}

export function useSharedRenderingEngine(): UseSharedRenderingEngineReturn {
  const [renderingEngine, setRenderingEngine] =
    useState<CornerstoneTypes.IRenderingEngine | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const renderingEngineRef = useRef<CornerstoneTypes.IRenderingEngine | null>(
    null
  );

  // Initialize Cornerstone and create the shared rendering engine
  useEffect(() => {
    let isMounted = true;

    async function initializeEngine() {
      try {
        // Initialize Cornerstone (centralized - handles core, tools, cache, loaders)
        await initializeCornerstone();

        // StrictMode cleanup may have run during the await — bail before
        // creating the engine to avoid duplicate engine IDs (the re-mount
        // effect will create its own engine).
        if (!isMounted) return;

        // Create the shared rendering engine
        const engine = new cornerstone.RenderingEngine(SHARED_ENGINE_ID);
        renderingEngineRef.current = engine;

        setRenderingEngine(engine);
        setIsInitialized(true);
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    }

    initializeEngine();

    return () => {
      isMounted = false;

      // Destroy the rendering engine on unmount
      if (renderingEngineRef.current) {
        try {
          renderingEngineRef.current.destroy();
        } catch {
          // Engine destruction error - may already be destroyed
        }
        renderingEngineRef.current = null;
      }
    };
  }, []);

  // Add a viewport to the shared engine
  const addViewport = useCallback(
    async (
      viewportId: string,
      element: HTMLDivElement,
      imageIds?: string[]
    ): Promise<CornerstoneTypes.IStackViewport | null> => {
      const engine = renderingEngineRef.current;
      if (!engine) {
        return null;
      }

      try {
        // Enable the element as a viewport
        engine.enableElement({
          viewportId,
          type: cornerstone.Enums.ViewportType.STACK,
          element,
        });

        const viewport = engine.getViewport(
          viewportId
        ) as CornerstoneTypes.IStackViewport;

        // Set stack if imageIds provided
        if (imageIds && imageIds.length > 0) {
          await viewport.setStack(imageIds);
          viewport.render();
        }

        return viewport;
      } catch {
        // Failed to add viewport
        return null;
      }
    },
    []
  );

  // Remove a viewport from the shared engine
  const removeViewport = useCallback((viewportId: string) => {
    const engine = renderingEngineRef.current;
    if (!engine) return;

    try {
      engine.disableElement(viewportId);
    } catch {
      // Viewport removal failed - may already be removed
    }
  }, []);

  // Get a viewport by ID
  const getViewport = useCallback(
    (viewportId: string): CornerstoneTypes.IStackViewport | null => {
      const engine = renderingEngineRef.current;
      if (!engine) return null;

      try {
        return engine.getViewport(
          viewportId
        ) as CornerstoneTypes.IStackViewport;
      } catch {
        return null;
      }
    },
    []
  );

  return {
    renderingEngine,
    renderingEngineId: SHARED_ENGINE_ID,
    isInitialized,
    error,
    addViewport,
    removeViewport,
    getViewport,
  };
}
